import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type {
  SecuritySubGroup,
  MenuItem,
  PersonListItem,
  ProjectListItem,
  LayerListItem,
} from '@services/adminConsoleService';
import adminConsoleService from '@services/adminConsoleService';

const CustomCheckbox: React.FC<{ checked: boolean; onChange: () => void }> = ({
  checked,
  onChange,
}) => (
  <button
    role="checkbox"
    aria-checked={checked}
    onClick={onChange}
    className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center transition-colors
      ${checked ? 'bg-white border-[#62aacb]' : 'bg-white border-[#ccc]'}`}
  >
    {checked && (
      <span
        className="material-icons text-[#62aacb]"
        style={{ fontSize: '18px', fontWeight: 'bolder', lineHeight: 1 }}
      >
        check
      </span>
    )}
  </button>
);

interface SecuritySubGroupCardProps {
  subGroup: SecuritySubGroup;
  onRename: (id: number, name: string) => void;
  onDelete: (id: number) => void;
  onUpdate: (id: number, updates: Partial<SecuritySubGroup>) => void;
}

type EditModal =
  | 'layers'
  | 'permissions'
  | 'cardtypes'
  | 'assignedto'
  | 'engagements'
  | 'whitelist'
  | null;

const PERM_FIELDS: { key: keyof SecuritySubGroup; icon: string; label: string }[] = [
  { key: 'View', icon: 'visibility', label: 'View' },
  { key: 'Delete', icon: 'delete', label: 'Delete' },
  { key: 'Temporary', icon: 'schedule', label: 'Temporary' },
  { key: 'ItemCreation', icon: 'check_circle', label: 'Create Items' },
  { key: 'Edit', icon: 'edit', label: 'Edit' },
  { key: 'IsBeta', icon: 'star', label: 'Beta' },
  { key: 'IsAdminGroup', icon: 'verified_user', label: 'Admin' },
];

export const SecuritySubGroupCard: React.FC<SecuritySubGroupCardProps> = ({
  subGroup,
  onRename,
  onDelete,
  onUpdate,
}) =>
{
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(subGroup.Name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  // ── Edit modal state ──
  const [editModal, setEditModal] = useState<EditModal>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Card types state
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [selectedCardTypeIds, setSelectedCardTypeIds] = useState<Set<number>>(
    new Set(subGroup.ConfigValueSecurityGroups.map(c => c.ConfigValueID)),
  );
  const [cardTypeSearch, setCardTypeSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [engagementsSearch, setEngagementsSearch] = useState('');

  // People list state
  const [peopleList, setPeopleList] = useState<PersonListItem[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<number>>(
    new Set(subGroup.PeopleSecurityGroup.map(p => p.PersonID ?? 0).filter(Boolean)),
  );

  // Projects list state
  const [projectsList, setProjectsList] = useState<ProjectListItem[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());

  // Whitelist state
  const [whitelistIPs, setWhitelistIPs] = useState<string[]>([]);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [savingWhitelist, setSavingWhitelist] = useState(false);

  // Layers list state
  const [layersList, setLayersList] = useState<LayerListItem[]>([]);
  const [loadingLayers, setLoadingLayers] = useState(false);
  const [layerSearch, setLayerSearch] = useState('');
  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<number>>(
    new Set(subGroup.SecurityGroupLayers.map(l => l.LayerId)),
  );

  // Permissions local edit state
  const [permEdit, setPermEdit] = useState({
    View: subGroup.View,
    Delete: subGroup.Delete,
    Temporary: subGroup.Temporary,
    ItemCreation: subGroup.ItemCreation,
    Edit: subGroup.Edit,
    IsBeta: subGroup.IsBeta,
    IsAdminGroup: subGroup.IsAdminGroup,
  });

  // Sync perm state when subGroup changes
  useEffect(() =>
  {
    setPermEdit({
      View: subGroup.View,
      Delete: subGroup.Delete,
      Temporary: subGroup.Temporary,
      ItemCreation: subGroup.ItemCreation,
      Edit: subGroup.Edit,
      IsBeta: subGroup.IsBeta,
      IsAdminGroup: subGroup.IsAdminGroup,
    });
  }, [
    subGroup.View,
    subGroup.Delete,
    subGroup.Temporary,
    subGroup.ItemCreation,
    subGroup.Edit,
    subGroup.IsBeta,
    subGroup.IsAdminGroup,
  ]);

  // ── Kebab menu ──
  useEffect(() =>
  {
    if (!menuOpen) return;
    const close = (e: MouseEvent) =>
    {
      const menu = document.querySelector('[data-ssg-menu]');
      if (menu && menu.contains(e.target as Node)) return;
      if (menuBtnRef.current && menuBtnRef.current.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const openMenu = () =>
  {
    if (!menuBtnRef.current) return;
    const rect = menuBtnRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right - 180 });
    setMenuOpen(true);
  };

  // ── Rename ──
  const commitRename = () =>
  {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== subGroup.Name) onRename(subGroup.SecurityGroupID, trimmed);
    else setNameValue(subGroup.Name);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape')
    {
      setNameValue(subGroup.Name);
      setEditing(false);
    }
  };

  // ── Save permissions ──
  const savePermissions = async () =>
  {
    setSaving(true);
    setModalError(null);
    try
    {
      await adminConsoleService.updateSecurityGroupPermissions(subGroup.SecurityGroupID, permEdit);
      onUpdate(subGroup.SecurityGroupID, permEdit);
      setEditModal(null);
    }
    catch
    {
      setModalError('Failed to save permissions. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const saveCardTypes = async () =>
  {
    setSaving(true);

    setModalError(null);

    try
    {
      const selected = menuItems.filter(i => selectedCardTypeIds.has(i.ConfigValueID));

      const payload = {
        ...subGroup,
        ConfigValueSecurityGroups: selected.map(i => ({
          ConfigValueID: i.ConfigValueID,
        })),
        PeopleSecurityGroup: subGroup.PeopleSecurityGroup || [],
      } as SecuritySubGroup;

      await adminConsoleService.updateCardsSecurityGroup(payload);

      onUpdate(subGroup.SecurityGroupID, {
        ConfigValueSecurityGroups: selected.map(i => ({
          ConfigValueID: i.ConfigValueID,
          Name: i.Text,
          Icon: i.Icon,
        })),
      });

      setEditModal(null);
      setCardTypeSearch('');
    }
    catch
    {
      setModalError('Failed to save card types.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const saveAssignedTo = async () =>
  {
    setSaving(true);
    setModalError(null);
    try
    {
      const selectedPeople = peopleList
        .filter(p => selectedPersonIds.has(p.PersonID))
        .map(p => ({ PersonID: p.PersonID }));

      const payload = {
        ...subGroup,
        PeopleSecurityGroup: selectedPeople,
      };

      await adminConsoleService.updatePersonsSecurityGroups(payload as SecuritySubGroup);

      const updated = peopleList
        .filter(p => selectedPersonIds.has(p.PersonID))
        .map(p => ({ PersonID: p.PersonID, Name: p.Name, PathImage: p.PathImage }));

      onUpdate(subGroup.SecurityGroupID, { PeopleSecurityGroup: updated });
      setEditModal(null);
      setAssignedSearch('');
    }
    catch
    {
      setModalError('Failed to save assigned users.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const saveEngagements = async () =>
  {
    setSaving(true);
    setModalError(null);

    try
    {
      const selected = projectsList.filter(p => selectedProjectIds.has(p.ProjectID));

      const payload = {
        ...subGroup,
        SecurityGroupProjects: selected.map(p => ({
          ProjectID: p.ProjectID,
          Name: p.Name,
        })),
      } as SecuritySubGroup;

      await adminConsoleService.updateProjectSecurityGroup(payload);

      onUpdate(subGroup.SecurityGroupID, {
        SecurityGroupProjects: selected.map(p => ({
          ProjectID: p.ProjectID,
          Name: p.Name,
        })),
      });
      setEditModal(null);
      setEngagementsSearch('');
    }
    catch
    {
      setModalError('Failed to save engagements.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const saveLayers = async () =>
  {
    setSaving(true);
    setModalError(null);

    try
    {
      const selected = layersList.filter(l => selectedLayerIds.has(l.LayerId));

      const payload = {
        ...subGroup,
        SecurityGroupLayers: selected.map(l => ({
          LayerId: l.LayerId,
        })),
      } as SecuritySubGroup;

      await adminConsoleService.updateLayersSecurityGroup(payload);

      onUpdate(subGroup.SecurityGroupID, {
        SecurityGroupLayers: selected.map(l => ({
          LayerId: l.LayerId,
          Name: l.Name,
          Color: l.Color,
          TextColor: l.TextColor,
        })),
      });

      setEditModal(null);
      setLayerSearch('');
    }
    catch
    {
      setModalError('Failed to save layers.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const openModal = (modal: EditModal) =>
  {
    setModalError(null);
    setEditModal(modal);
    if (modal === 'whitelist')
    {
      const raw = subGroup.WhiteListIPAddress || '';
      setWhitelistIPs(
        raw
          ? raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
          : [],
      );
      setWhitelistInput('');
    }
    if (modal === 'cardtypes')
    {
      setSelectedCardTypeIds(new Set(subGroup.ConfigValueSecurityGroups.map(c => c.ConfigValueID)));
      setLoadingMenu(true);
      adminConsoleService
        .getMenuItems()
        .then(items => setMenuItems(items))
        .catch(() => setModalError('Failed to load card types.'))
        .finally(() => setLoadingMenu(false));
    }
    if (modal === 'layers')
    {
      setSelectedLayerIds(new Set(subGroup.SecurityGroupLayers.map(l => l.LayerId)));
      setLoadingLayers(true);
      adminConsoleService
        .getLayersList()
        .then(layers => setLayersList(layers))
        .catch(() => setModalError('Failed to load layers.'))
        .finally(() => setLoadingLayers(false));
    }
    if (modal === 'engagements')
    {
      setLoadingProjects(true);
      adminConsoleService
        .getAllProjects()
        .then(projects =>
        {
          setProjectsList(projects);
          const assignedNames = new Set(subGroup.SecurityGroupProjects.map(p => p.Name));
          setSelectedProjectIds(
            new Set(projects.filter(p => assignedNames.has(p.Name)).map(p => p.ProjectID)),
          );
        })
        .catch(() => setModalError('Failed to load projects.'))
        .finally(() => setLoadingProjects(false));
    }
    if (modal === 'assignedto')
    {
      setLoadingPeople(true);
      adminConsoleService
        .getPeopleList()
        .then(people =>
        {
          setPeopleList(people);
          const assignedNames = new Set(subGroup.PeopleSecurityGroup.map(p => p.Name));
          setSelectedPersonIds(
            new Set(people.filter(p => assignedNames.has(p.Name)).map(p => p.PersonID)),
          );
        })
        .catch(() => setModalError('Failed to load people.'))
        .finally(() => setLoadingPeople(false));
    }
  };

  const editLink = (modal: EditModal) => (
    <button
      onClick={() => openModal(modal)}
      className="text-[11px] text-[#62aacb] hover:underline ml-1"
    >
      Edit
    </button>
  );

  // ── Shared modal shell ──
  const modalShell = (
    title: string,
    search: { value: string; onChange: (v: string) => void } | null,
    listHeader: string,
    rows: React.ReactNode,
    onSave: (() => void) | null,
  ) =>
    createPortal(
      <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" onClick={() => setEditModal(null)} />
        <div className="relative z-10 bg-white rounded shadow-xl w-[460px] max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <h3 className="text-[16px] font-medium text-[#62aacb]">{title}</h3>
            <button onClick={() => setEditModal(null)} className="text-[#bbb] hover:text-[#666]">
              <span className="material-icons text-[18px]">close</span>
            </button>
          </div>
          {/* Search */}
          {search && (
            <div className="flex items-center gap-3 px-5 pb-3">
              <span className="text-[12px] text-[#555] flex-shrink-0">Find by keyword:</span>
              <input
                autoFocus
                type="text"
                value={search.value}
                onChange={e => search.onChange(e.target.value)}
                className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
              />
            </div>
          )}
          {/* List */}
          <div className="flex-1 overflow-hidden mx-5 mb-3 border border-[#ddd] rounded flex flex-col">
            <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
              {listHeader}
            </div>
            <div className="flex-1 overflow-y-auto">{rows}</div>
          </div>
          {/* Footer */}
          <div className="px-5 py-3 border-t border-[#eee] flex justify-end gap-2">
            {modalError && <span className="text-[11px] text-[#e57373] flex-1">{modalError}</span>}
            <button
              onClick={() => setEditModal(null)}
              className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777] dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
            >
              Cancel
            </button>
            {onSave ? (
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            ) : (
              <button
                onClick={() => setEditModal(null)}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba]"
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );

  // ── Permissions modal ──
  const permissionsModal = modalShell(
    'Permissions',
    null,
    'Permission',
    <>
      {PERM_FIELDS.map(({ key, icon, label }) => (
        <label
          key={key}
          className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
        >
          <CustomCheckbox
            checked={!!permEdit[key as keyof typeof permEdit]}
            onChange={() => setPermEdit(p => ({ ...p, [key]: !p[key as keyof typeof permEdit] }))}
          />
          <span
            className={`material-icons text-[16px] w-5 flex-shrink-0 ${permEdit[key as keyof typeof permEdit] ? 'text-[#62aacb]' : 'text-[#ccc]'}`}
          >
            {icon}
          </span>
          <span className="text-[16px] text-[#333]" style={{ fontWeight: '400' }}>
            {label}
          </span>
        </label>
      ))}
    </>,
    savePermissions,
  );

  // ── Card Types modal content ──
  const toggleCardType = (id: number) =>
  {
    setSelectedCardTypeIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredMenuItems = menuItems.filter(item =>
    item.Text.toLowerCase().includes(cardTypeSearch.toLowerCase()),
  );

  const cardTypesModal = modalShell(
    'Card Types',
    { value: cardTypeSearch, onChange: setCardTypeSearch },
    'Name',
    loadingMenu ? (
      <div className="flex items-center justify-center py-8">
        <span className="material-icons animate-spin text-[#62aacb] text-[24px]">refresh</span>
      </div>
    ) : filteredMenuItems.length === 0 ? (
      <div className="text-[12px] text-[#aaa] text-center py-6 italic">No results found.</div>
    ) : (
      <>
        {filteredMenuItems.map(item => (
          <label
            key={item.ConfigValueID}
            className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
          >
            <CustomCheckbox
              checked={selectedCardTypeIds.has(item.ConfigValueID)}
              onChange={() => toggleCardType(item.ConfigValueID)}
            />
            <span className="text-[#666] text-[15px] w-5 flex-shrink-0 flex items-center">
              <i className={item.Icon} />
            </span>
            <span className="text-[16px] text-[#333]" style={{ fontWeight: '400' }}>
              {item.Text}
            </span>
          </label>
        ))}
      </>
    ),
    saveCardTypes,
  );

  // ── Assigned To modal ──
  const togglePerson = (id: number) =>
  {
    setSelectedPersonIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredPeople = peopleList.filter(p =>
    p.Name.toLowerCase().includes(assignedSearch.toLowerCase()),
  );

  const assignedToModal = modalShell(
    'Assigned To',
    { value: assignedSearch, onChange: setAssignedSearch },
    'Name',
    loadingPeople ? (
      <div className="flex items-center justify-center py-8">
        <span className="material-icons animate-spin text-[#62aacb] text-[24px]">refresh</span>
      </div>
    ) : filteredPeople.length === 0 ? (
      <div className="text-[12px] text-[#aaa] text-center py-6 italic">
        {assignedSearch ? 'No results found.' : 'No people found.'}
      </div>
    ) : (
      <>
        {[...filteredPeople]
          .sort((a, b) =>
          {
            const aChecked = selectedPersonIds.has(a.PersonID) ? 1 : 0;
            const bChecked = selectedPersonIds.has(b.PersonID) ? 1 : 0;
            return bChecked - aChecked;
          })
          .map(p => (
            <label
              key={p.PersonID}
              className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
            >
              <CustomCheckbox
                checked={selectedPersonIds.has(p.PersonID)}
                onChange={() => togglePerson(p.PersonID)}
              />
              {p.PathImage ? (
                <div
                  className="w-[26px] h-[26px] rounded-sm bg-cover bg-center border border-[#e0e0e0] flex-shrink-0"
                  style={{ backgroundImage: `url(${p.PathImage})` }}
                />
              ) : (
                <div className="w-[26px] h-[26px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0] flex-shrink-0">
                  <span className="material-icons text-[14px] text-[#999]">person</span>
                </div>
              )}
              <span className="text-[16px] text-[#333]" style={{ fontWeight: '400' }}>
                {p.Name}
              </span>
            </label>
          ))}
      </>
    ),
    saveAssignedTo,
  );

  // ── Engagements modal ──
  const toggleProject = (id: number) =>
  {
    setSelectedProjectIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredProjects = projectsList.filter(p =>
    p.Name.toLowerCase().includes(engagementsSearch.toLowerCase()),
  );

  const engagementsModal = modalShell(
    'Engagements',
    { value: engagementsSearch, onChange: setEngagementsSearch },
    'Name',
    loadingProjects ? (
      <div className="flex items-center justify-center py-8">
        <span className="material-icons animate-spin text-[#62aacb] text-[24px]">refresh</span>
      </div>
    ) : filteredProjects.length === 0 ? (
      <div className="text-[12px] text-[#aaa] text-center py-6 italic">
        {engagementsSearch ? 'No results found.' : 'No projects found.'}
      </div>
    ) : (
      <>
        {filteredProjects.map((p, index) => (
          <label
            key={`${p.ProjectID}-${index}`}
            className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
          >
            <CustomCheckbox
              checked={selectedProjectIds.has(p.ProjectID)}
              onChange={() => toggleProject(p.ProjectID)}
            />
            <span className="material-icons text-[16px] text-[#666] flex-shrink-0">business</span>
            <span className="text-[16px] text-[#333]" style={{ fontWeight: '400' }}>
              {p.Name}
            </span>
          </label>
        ))}
      </>
    ),
    saveEngagements,
  );

  // ── Layers modal content ──
  // ── Layers modal ──
  const toggleLayer = (id: number) =>
  {
    setSelectedLayerIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredLayers = layersList.filter(l =>
    l.Name.toLowerCase().includes(layerSearch.toLowerCase()),
  );

  const layersModal = modalShell(
    'Layers',
    { value: layerSearch, onChange: setLayerSearch },
    'Layer',
    loadingLayers ? (
      <div className="flex items-center justify-center py-8">
        <span className="material-icons animate-spin text-[#62aacb] text-[24px]">refresh</span>
      </div>
    ) : filteredLayers.length === 0 ? (
      <div className="text-[12px] text-[#aaa] text-center py-6 italic">
        {layerSearch ? 'No results found.' : 'No layers found.'}
      </div>
    ) : (
      <>
        {filteredLayers.map(l => (
          <label
            key={l.LayerId}
            className="flex items-center gap-3 px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9] cursor-pointer"
          >
            <CustomCheckbox
              checked={selectedLayerIds.has(l.LayerId)}
              onChange={() => toggleLayer(l.LayerId)}
            />
            <div
              className="w-4 h-4 rounded flex-shrink-0 border border-[#ddd]"
              style={{ backgroundColor: l.Color === 'transparent' ? '#fff' : l.Color }}
            />
            <span className="text-[16px] text-[#333]" style={{ fontWeight: '400' }}>
              {l.Name}
            </span>
          </label>
        ))}
      </>
    ),
    saveLayers,
  );

  // ── Whitelist modal ──
  const addWhitelistIP = () =>
  {
    const ip = whitelistInput.trim();
    if (!ip || whitelistIPs.includes(ip)) return;
    setWhitelistIPs(prev => [...prev, ip]);
    setWhitelistInput('');
  };

  const removeWhitelistIP = (ip: string) =>
  {
    setWhitelistIPs(prev => prev.filter(x => x !== ip));
  };

  const saveWhitelist = async () =>
  {
    setSavingWhitelist(true);
    setModalError(null);
    try
    {
      const value = whitelistIPs.join(',');
      await adminConsoleService.updateWhiteListIPAddress(subGroup.SecurityGroupID, value);
      onUpdate(subGroup.SecurityGroupID, { WhiteListIPAddress: value });
      setEditModal(null);
    }
    catch
    {
      setModalError('Failed to save IP whitelist. Please try again.');
    }
    finally
    {
      setSavingWhitelist(false);
    }
  };

  const whitelistModal = createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={() => setEditModal(null)} />
      <div className="relative z-10 bg-white rounded shadow-xl w-[460px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <h3 className="text-[16px] font-medium text-[#62aacb]">IP Address White List</h3>
          <button onClick={() => setEditModal(null)} className="text-[#bbb] hover:text-[#666]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Add row */}
        <div className="flex items-center gap-3 px-5 pb-3">
          <span className="text-[12px] text-[#555] flex-shrink-0">Write IP address:</span>
          <input
            autoFocus
            type="text"
            value={whitelistInput}
            onChange={e => setWhitelistInput(e.target.value)}
            onKeyDown={e =>
            {
              if (e.key === 'Enter') addWhitelistIP();
            }}
            placeholder="e.g. 192.168.1.1 or *"
            className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
          />
          <button
            onClick={addWhitelistIP}
            className="px-3 py-1 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] flex-shrink-0"
          >
            Add
          </button>
        </div>

        {/* List */}
        <div
          className="mx-5 mb-4 border border-[#ddd] rounded flex flex-col"
          style={{ minHeight: 180 }}
        >
          <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
            IP Address
          </div>
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 220 }}>
            {whitelistIPs.length === 0 ? (
              <div className="text-[12px] text-[#aaa] text-center py-6 italic">
                No IP addresses added.
              </div>
            ) : (
              whitelistIPs.map(ip => (
                <div
                  key={ip}
                  className="flex items-center justify-between px-3 py-2 border-b border-[#f0f0f0] hover:bg-[#f9f9f9]"
                >
                  <span className="text-[16px] text-[#333] font-mono" style={{ fontWeight: '400' }}>
                    {ip}
                  </span>
                  <button
                    onClick={() => removeWhitelistIP(ip)}
                    className="text-[#bbb] hover:text-[#e57373] transition-colors"
                  >
                    <span className="material-icons text-[16px]">close</span>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Error */}
        {modalError && <p className="text-[11px] text-[#e57373] px-5 pb-2">{modalError}</p>}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#eee] flex justify-end gap-2">
          <button
            onClick={() => setEditModal(null)}
            className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777]"
          >
            Cancel
          </button>
          <button
            onClick={saveWhitelist}
            disabled={savingWhitelist}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
          >
            {savingWhitelist ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <div className="border border-[#e0e0e0] rounded bg-white mb-2 relative">
      {/* ── Top row: layers + menu ── */}
      <div className="flex items-center justify-end gap-1 px-3 pt-2 pb-1 flex-wrap">
        {subGroup.SecurityGroupLayers.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap flex-1 justify-end">
            {editLink('layers')}
            {subGroup.SecurityGroupLayers.map(l => (
              <span
                key={l.LayerId}
                className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: l.Color, color: l.TextColor ? '#000' : '#fff' }}
              >
                {l.Name}
              </span>
            ))}
          </div>
        )}
        <button
          ref={menuBtnRef}
          onClick={openMenu}
          className="text-[#bbb] hover:text-[#666] transition-colors ml-1 flex-shrink-0"
          title="Options"
        >
          <span className="material-icons text-[18px]">more_vert</span>
        </button>
      </div>

      {/* ── Name + description ── */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2">
          <i className="material-icons text-[18px] text-[#555]">people</i>
          {editing ? (
            <input
              autoFocus
              type="text"
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={handleKeyDown}
              className="border border-[#62aacb] rounded px-1 py-0.5 text-[14px] font-semibold text-[#333] outline-none w-full max-w-[300px]"
            />
          ) : (
            <span
              className="text-[14px] font-semibold text-[#333] cursor-text"
              onDoubleClick={() => setEditing(true)}
              title="Double click to rename"
            >
              {subGroup.Name}
            </span>
          )}
          {subGroup.IsAdminGroup && (
            <span className="text-[10px] px-1.5 py-0.5 bg-[#62aacb] text-white rounded-full">
              Admin
            </span>
          )}
        </div>
        {subGroup.Description && (
          <p className="text-[12px] text-[#888] mt-0.5 ml-7">{subGroup.Description}</p>
        )}
      </div>

      {/* ── Details row ── */}
      <div
        className="px-3 pb-3 grid gap-x-4 gap-y-2 text-[11px] border-t border-[#f0f0f0] pt-2"
        style={{ gridTemplateColumns: '1fr 1fr 1fr 2fr' }}
      >
        {/* Permissions */}
        <div>
          <span className="text-[#555]">Permissions:</span>
          {editLink('permissions')}
          <div className="flex items-center gap-1 mt-1">
            <span
              title="View"
              className={`material-icons text-[16px] ${subGroup.View ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              visibility
            </span>
            <span
              title="Delete"
              className={`material-icons text-[16px] ${subGroup.Delete ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              delete
            </span>
            <span
              title="Temp"
              className={`material-icons text-[16px] ${subGroup.Temporary ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              schedule
            </span>
            <span
              title="Create"
              className={`material-icons text-[16px] ${subGroup.ItemCreation ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              check_circle
            </span>
            <span
              title="Edit"
              className={`material-icons text-[16px] ${subGroup.Edit ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              edit
            </span>
            <span
              title="Beta"
              className={`material-icons text-[16px] ${subGroup.IsBeta ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              star
            </span>
            <span
              title="Admin"
              className={`material-icons text-[16px] ${subGroup.IsAdminGroup ? 'text-[#62aacb]' : 'text-[#ddd]'}`}
            >
              verified_user
            </span>
          </div>
        </div>

        {/* Card Types */}
        <div>
          <span className="text-[#555]">Card Types:</span>
          {editLink('cardtypes')}
          <div className="flex items-center gap-2 mt-1">
            {subGroup.ConfigValueSecurityGroups.length === 0 ? (
              <span className="text-[#ccc]">—</span>
            ) : (
              <>
                {subGroup.ConfigValueSecurityGroups.slice(0, 6).map(c => (
                  <span key={c.ConfigValueID} title={c.Name} className="text-[#666] text-[16px]">
                    <i className={c.Icon} />
                  </span>
                ))}
                {subGroup.ConfigValueSecurityGroups.length > 6 && (
                  <span className="text-[#999]">
                    +{subGroup.ConfigValueSecurityGroups.length - 6}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Assigned to */}
        <div>
          <span className="text-[#555]">Assigned to:</span>
          {editLink('assignedto')}
          <div className="flex items-center gap-0.5 mt-1">
            {subGroup.PeopleSecurityGroup.length === 0 ? (
              <span className="text-[#ccc]">—</span>
            ) : (
              <>
                {subGroup.PeopleSecurityGroup.slice(0, 6).map((p, i) =>
                  p.PathImage ? (
                    <div
                      key={i}
                      title={p.Name}
                      className="w-[22px] h-[22px] rounded-sm bg-[#ddd] bg-cover bg-center border border-[#e0e0e0] flex-shrink-0"
                      style={{ backgroundImage: `url(${p.PathImage})` }}
                    />
                  ) : (
                    <div
                      key={i}
                      title={p.Name}
                      className="w-[22px] h-[22px] rounded-sm bg-[#ddd] flex items-center justify-center border border-[#e0e0e0] flex-shrink-0"
                    >
                      <span className="material-icons text-[14px] text-[#999]">person</span>
                    </div>
                  ),
                )}
                {subGroup.PeopleSecurityGroup.length > 6 && (
                  <span className="text-[#999] ml-1">
                    +{subGroup.PeopleSecurityGroup.length - 6}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Engagements / Projects */}
        <div className="min-w-0">
          <span className="text-[#555]">Engagements:</span>
          {editLink('engagements')}
          <div className="mt-1 text-[#555] truncate">
            {subGroup.SecurityGroupProjects.length === 0 ? (
              <span className="text-[#ccc]">—</span>
            ) : (
              <>
                {subGroup.SecurityGroupProjects.slice(0, 4)
                  .map(p => p.Name)
                  .join(', ')}
                {subGroup.SecurityGroupProjects.length > 4 && (
                  <span className="text-[#999]"> +{subGroup.SecurityGroupProjects.length - 4}</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Kebab dropdown (portal) ── */}
      {menuOpen &&
        createPortal(
          <div
            data-ssg-menu
            className="admin-console-portal fixed z-[10002] bg-white border border-[#ddd] rounded shadow-lg py-1 w-[180px] text-[12px] dark:bg-[#3b3d3d] dark:border-[transparent]"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              className="w-full text-left px-3 py-2 hover:bg-[#f5f5f5] text-[#333] dark:hover:bg-[#4a4c4c] flex items-center gap-2"
              onClick={() =>
              {
                setMenuOpen(false);
                openModal('whitelist');
              }}
            >
              <i className="icon-pencil-icon text-[#888]" />
              Manage IP Address White List
            </button>
            <div className="border-t border-[#eee] my-1" />
            <button
              className="w-full text-left px-3 py-2 hover:bg-[#fdecea] dark:hover:bg-[#4a4c4c] text-[#e57373] flex items-center gap-2"
              onClick={() =>
              {
                setMenuOpen(false);
                onDelete(subGroup.SecurityGroupID);
              }}
            >
              <i className="icon-trash-icon" />
              Delete Security Sub-Group
            </button>
          </div>,
          document.body,
        )}

      {/* ── Edit modals ── */}
      {editModal === 'permissions' && permissionsModal}
      {editModal === 'cardtypes' && cardTypesModal}
      {editModal === 'assignedto' && assignedToModal}
      {editModal === 'engagements' && engagementsModal}
      {editModal === 'layers' && layersModal}
      {editModal === 'whitelist' && whitelistModal}
    </div>
  );
};
